import type { PviGraphWithSpans } from '../../editor/pviTypes';
import { useAdversarialStore } from '../../stores/useAdversarialStore';
import { ScenarioSelector } from './ScenarioSelector';
import { ResultsView } from './ResultsView';
import { TraceView } from './TraceView';
import styles from './Adversarial.module.css';

interface AdversarialPanelProps {
  graph: PviGraphWithSpans;
}

type AdvTab = 'scenarios' | 'results' | 'trace';

export function AdversarialPanel({ graph }: AdversarialPanelProps) {
  const result = useAdversarialStore(s => s.result);
  const selectedScenario = useAdversarialStore(s => s.selectedScenario);
  const selectedRunId = useAdversarialStore(s => s.selectedRunId);

  // Auto-select tab based on state
  let activeTab: AdvTab = 'scenarios';
  if (selectedRunId !== null) {
    activeTab = 'trace';
  } else if (selectedScenario !== null) {
    activeTab = 'results';
  }

  return (
    <div className={styles.advPanel} data-testid="adversarial-panel">
      {/* Summary banner */}
      {result && (
        <div className={styles.advHeader}>
          <span className={styles.advTitle}>Adversarial Analysis</span>
          <div className={styles.advSummary}>
            <span>{result.totalRuns} runs</span>
            {result.totalFailures > 0 && (
              <span className={`${styles.summaryBadge} ${styles.badgeFail}`}>
                {result.totalFailures} failures
              </span>
            )}
            {result.totalWarnings > 0 && (
              <span className={`${styles.summaryBadge} ${styles.badgeWarn}`}>
                {result.totalWarnings} warnings
              </span>
            )}
            {result.totalFailures === 0 && result.totalWarnings === 0 && (
              <span className={`${styles.summaryBadge} ${styles.badgePass}`}>
                All passed
              </span>
            )}
          </div>
        </div>
      )}

      {/* Guardrail banner */}
      {result && result.totalFailures > 0 && (
        <div className={styles.guardrailBanner}>
          <span className={styles.guardrailIcon}>⚠</span>
          <span>Adversarial Risks Detected — review failures before deployment</span>
        </div>
      )}

      {/* Content area */}
      {activeTab === 'scenarios' && <ScenarioSelector graph={graph} />}
      {activeTab === 'results' && <ResultsView />}
      {activeTab === 'trace' && <TraceView />}
    </div>
  );
}
